import React from 'react';

import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import { getDeviceFeatures } from '@/components/FeaturesFlags/actions';
import { menuSystem } from '@/components/FeaturesFlags/menuSystem';
import BaseLanguageSelector from '@jpi-cloud-web-pro/jpi-cloud-shared/components/BaseLanguageSelector';
import showToast from '@jpi-cloud-web-pro/jpi-cloud-shared/components/Notification';
import { languageOptions } from '@jpi-cloud-web-pro/jpi-cloud-shared/constants/languages';

import { selectLanguage } from './actions';

import logger from '@/utils/logger';

export type Language = {
  displayName: string;
  value: string;
  name: string;
  id: string;
};

type LanguageSelectorProps = {
  menuType?: string;
  languages?: Language[];
  selectedLanguage?: string;
  selectedSystem?: { devices: Array<{ id: string }> };
  selectLanguage: (language: string) => Promise<void>;
  getDeviceFeatures: (deviceId: string) => Promise<void>;
  userLoggedIn: boolean;
  showIcon?: boolean;
  size?: 'small' | 'medium';
  iconSize?: '1x' | '2x' | '3x' | '4x' | '5x';
};

export function LanguageSelector({
  menuType,
  languages = [],
  showIcon = true,
  size = 'medium',
  iconSize = '1x',
  selectedLanguage,
  selectedSystem,
  selectLanguage,
  getDeviceFeatures,
  userLoggedIn,
}: LanguageSelectorProps) {
  const changeLanguage = async newLanguage => {
    try {
      await selectLanguage(newLanguage);

      if (!userLoggedIn) {
        return;
      }

      const [mainDevice] = selectedSystem?.devices || [];

      if (mainDevice?.id && menuType === menuSystem.simplified) {
        await getDeviceFeatures(mainDevice.id);
      }
    } catch (error) {
      showToast(
        'error',
        <FormattedMessage
          id="profile-settings.profile-info.error"
          defaultMessage="There is an error with your user profile information. Please go to profile page and update accordingly."
        />,
      );

      logger.toAppInsights(error);
    }
  };

  const parsedLanguages = userLoggedIn
    ? (languages ?? []).map(lang => {
        const [value] = lang.name.split('-');
        return { ...lang, value };
      })
    : languageOptions;

  return (
    <BaseLanguageSelector
      languages={parsedLanguages}
      showIcon={showIcon}
      size={size}
      iconSize={iconSize}
      changeLanguage={changeLanguage}
      selectedLanguage={selectedLanguage}
    />
  );
}

const mapStateToProps = ({
  app: { languages, selectedSystem, userLoggedIn },
  language: { selectedLanguage },
  features: { deviceFeatures: { menuSystem = undefined } = {} },
}) => ({
  languages,
  selectedLanguage,
  menuType: menuSystem,
  selectedSystem,
  userLoggedIn,
});

const mapDispatchToProps = { selectLanguage, getDeviceFeatures };

export default connect(mapStateToProps, mapDispatchToProps)(LanguageSelector);
